import { useEffect } from "react";

export default function TermsConditionsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="wrapper cms-pages">
      <div className="title mb-10 md:pb-14 text-center">
        <h1>Terms & Conditions</h1>
        <p className="legal-updated">Last Updated: April 9, 2026</p>
      </div>
      <div className="">
        <p>
          Welcome to Smora. These Terms and Conditions govern your use of our
          mobile application and services. By accessing or using our app, you
          agree to be bound by these terms. Please read them carefully.
        </p>

        <h2>1. Acceptance of Terms</h2>
        <p>
          By creating an account or using our services, you acknowledge that you
          have read, understood, and agree to be bound by these Terms and
          Conditions, as well as our Privacy Policy. If you do not agree with
          any part of these terms, you must not use our services.
        </p>

        <h2>2. Eligibility</h2>
        <p>To use our services, you must:</p>
        <ul>
          <li>Be at least 18 years of age or have parental/guardian consent</li>
          <li>Have the legal capacity to enter into binding contracts</li>
          <li>Provide accurate and complete registration information</li>
          <li>Maintain the security of your account credentials</li>
        </ul>

        <h2>3. Account Registration</h2>
        <h3>3.1 Account Creation</h3>
        <p>
          You must create an account to access certain features of our app. You
          are responsible for maintaining the confidentiality of your account
          information and password.
        </p>

        <h3>3.2 Account Security</h3>
        <ul>
          <li>You are responsible for all activities under your account</li>
          <li>Notify us immediately of any unauthorized access</li>
          <li>Do not share your account credentials with others</li>
          <li>Use a strong, unique password</li>
        </ul>

        <h2>4. Product Information and Pricing</h2>
        <h3>4.1 Product Descriptions</h3>
        <p>
          We strive to provide accurate product descriptions, images, and
          specifications for all gold and silver jewellery items. Slight
          variations in colour or finish may occur due to photography lighting
          and display settings. Actual product weight and dimensions are as
          specified on the product page.
        </p>

        <h3>4.2 Gold & Silver Pricing</h3>
        <p>
          Jewellery prices on Smora are dynamic and based on the following
          components:
        </p>
        <ul>
          <li>All prices are listed in Indian Rupees (INR)</li>
          <li>
            Gold jewellery prices are linked to live MCX/international spot gold
            rates
          </li>
          <li>
            Silver jewellery prices are linked to live MCX/international spot
            silver rates
          </li>
          <li>
            Prices are subject to change based on prevailing metal rates at the
            time of order confirmation
          </li>
          <li>
            We reserve the right to correct pricing errors before order
            processing
          </li>
        </ul>

        <h3>4.3 Metal Purity</h3>
        <p>
          All gold and silver products are clearly labelled with their purity:
        </p>
        <ul>
          <li>Gold: 24K (99.9%), 22K (91.6%), 18K (75%)as applicable</li>
          <li>
            Silver: 999 (pure silver), 925 (sterling silver) as applicable
          </li>
          <li>
            Purity is verified through BIS Hallmarking and third-party
            certification
          </li>
        </ul>

        <h3>4.4 Product Availability</h3>
        <p>
          Product availability is subject to change based on stock, metal
          availability, and custom order lead times. We reserve the right to
          limit quantities or discontinue products at any time.
        </p>

        <h2>5. Orders and Payments</h2>
        <h3>5.1 Order Placement</h3>
        <ol>
          <li>Browse and select your gold or silver jewellery items</li>
          <li>
            Add items to your cart and review the order summary including metal
            rates, making charges, and GST
          </li>
          <li>
            Provide a valid shipping address and complete KYC if required for
            high-value purchases
          </li>
          <li>Choose a payment method and complete the transaction</li>
          <li>
            Receive order confirmation with invoice via email or app
            notification
          </li>
        </ol>

        <h3>5.2 Order Acceptance and Metal Rate Lock</h3>
        <p>
          Your order constitutes an offer to purchase at the metal rate
          prevailing at the time of payment confirmation. Prices are locked only
          upon successful payment. We reserve the right to cancel orders in
          cases of pricing errors, stock unavailability, or suspected fraudulent
          activity, with a full refund issued.
        </p>

        <h3>5.3 KYC Requirements</h3>
        <p>
          As per regulatory requirements, purchases exceeding ₹2,00,000 in value
          require submission of a valid PAN card. Additional KYC documents may
          be required for higher-value orders or as mandated by applicable law.
        </p>

        <h3>5.4 Payment Methods</h3>
        <ul>
          <li>Credit/Debit cards (Visa, Mastercard, RuPay)</li>
          <li>Net banking</li>
          <li>UPI and digital wallets (GPay, PhonePe, Paytm)</li>
          <li>
            EMI options on select bank cards for orders above a specified amount
          </li>
        </ul>

        <h3>5.5 Payment Security</h3>
        <p>
          All payments are processed through secure, PCI-DSS compliant payment
          gateways. We do not store your complete payment card information on
          our servers. All transactions are encrypted using 256-bit SSL
          technology.
        </p>

        <h2>6. Shipping and Delivery</h2>
        <h3>6.1 Shipping Policy</h3>
        <ul>
          <li>
            All jewellery orders are shipped with full insurance coverage for
            the declared value
          </li>
          <li>Orders are dispatched in tamper-proof, secure packaging</li>
          <li>
            Delivery times vary based on location — typically 5–10 business days
            within India
          </li>
          <li>
            Shipping charges are calculated at checkout (free shipping may apply
            above a certain order value)
          </li>
          <li>We currently ship to addresses within India only</li>
          <li>
            Track your order in real-time through the app using your tracking
            number
          </li>
        </ul>

        <h3>6.2 Delivery Issues</h3>
        <p>
          If you experience delivery issues, please contact our customer support
          within 48 hours of the expected delivery date. For lost or damaged
          shipments, we will initiate an insurance claim on your behalf.
        </p>

        <h2>7. Returns and Refunds</h2>
        <h3>7.1 Return Policy</h3>
        <p>
          We offer a 15-day return and exchange policy on eligible jewellery
          products:
        </p>
        <ul>
          <li>Products must be unworn and in original condition</li>
          <li>Original packaging, tags, and hallmark labels must be intact</li>
          <li>Certificate of authenticity and invoice must be included</li>
          <li>
            BIS Hallmark verification report (if provided) must be returned
          </li>
          <li>
            Customized, engraved, or personalized items are non-returnable
          </li>
          <li>
            Items showing signs of wear, damage, or alteration will not be
            accepted
          </li>
        </ul>

        <h3>7.2 Exchange Rate on Returns</h3>
        <p>
          Gold and silver jewellery returns are subject to the prevailing metal
          rate at the time of return processing, not the rate at original
          purchase. Making charges and GST paid are non-refundable.
        </p>

        <h3>7.3 Refund Process</h3>
        <ol>
          <li>
            Initiate a return request through the app within 15 days of delivery
          </li>
          <li>
            Our team will arrange a pickup or guide you to ship the product back
            securely
          </li>
          <li>
            We inspect and verify the returned jewellery including hallmark and
            condition
          </li>
          <li>
            Refund is processed within 7–10 business days after successful
            inspection
          </li>
          <li>
            Refund amount reflects prevailing metal rates at the time of return
            processing
          </li>
        </ol>

        <h2>8. Product Authenticity and Certification</h2>
        <p>
          All gold and silver jewellery sold on Smora is 100% authentic and
          comes with:
        </p>
        <ul>
          <li>
            BIS Hallmark certification (mandatory for gold jewellery as per
            Government of India directives)
          </li>
          <li>
            Certificate of authenticity issued by the manufacturer or
            third-party assaying lab
          </li>
          <li>
            HUID (Hallmark Unique Identification) number for gold items,
            traceable on the BIS Care portal
          </li>
          <li>
            Detailed product specifications including gross weight, net weight,
            stone details, and metal purity
          </li>
          <li>
            Quality assurance guarantee backed by our trusted seller network
          </li>
        </ul>

        <h2>9. Intellectual Property</h2>
        <p>
          All content on our app, including text, graphics, logos, images, and
          software, is the property of Smora or its licensors and is protected
          by copyright, trademark, and other intellectual property laws.
        </p>

        <h3>9.1 Prohibited Uses</h3>
        <p>You may not:</p>
        <ul>
          <li>Copy, modify, or distribute our content without permission</li>
          <li>Use our trademarks or branding without authorization</li>
          <li>Reverse engineer or decompile our app</li>
          <li>Create derivative works based on our content</li>
        </ul>

        <h2>10. User Conduct</h2>
        <p>You agree not to:</p>
        <ul>
          <li>Use the app for any unlawful purpose</li>
          <li>Impersonate any person or entity</li>
          <li>Interfere with the app's functionality or security</li>
          <li>Upload malicious code or viruses</li>
          <li>Harass, abuse, or harm other users</li>
          <li>Engage in fraudulent activities</li>
        </ul>

        <h2>11. Limitation of Liability</h2>
        <p>
          To the maximum extent permitted by law, Smora shall not be liable for
          any indirect, incidental, special, consequential, or punitive damages
          arising from your use of our services, including but not limited to
          loss of profits, data, or goodwill.
        </p>

        <h2>12. Indemnification</h2>
        <p>
          You agree to indemnify and hold harmless Smora, its affiliates, and
          their respective officers, directors, employees, and agents from any
          claims, damages, losses, or expenses arising from your violation of
          these Terms or your use of our services.
        </p>

        <h2>13. Dispute Resolution</h2>
        <h3>13.1 Governing Law</h3>
        <p>
          These Terms shall be governed by and construed in accordance with the
          laws of India.
        </p>

        <h3>13.2 Jurisdiction</h3>
        <p>
          Any disputes arising from these Terms shall be subject to the
          exclusive jurisdiction of the courts in Mumbai, India.
        </p>

        <h2>14. Modifications to Terms</h2>
        <p>
          We reserve the right to modify these Terms at any time. We will notify
          you of material changes by posting the updated Terms on our app and
          updating the "Last Updated" date. Your continued use of our services
          after such changes constitutes acceptance of the modified Terms.
        </p>

        <h2>15. Termination</h2>
        <p>
          We reserve the right to suspend or terminate your account and access
          to our services at any time, with or without notice, for any reason,
          including violation of these Terms.
        </p>

        <h2>16. Contact Information</h2>
        <p>
          If you have any questions about these Terms and Conditions, please
          contact us:
        </p>
        <ul>
          <li>Email: info@smorajewellery.com</li>
          <li>Phone: +91 70692 57283</li>
          <li>Address: Mendarda, Gujarat, India - 362260</li>
        </ul>

        <div className="legal-footer">
          <p>
            By using Smora, you acknowledge that you have read, understood, and
            agree to be bound by these Terms and Conditions.
          </p>
        </div>
      </div>
    </div>
  );
}
